"use client"

import { Loader2, SearchIcon, X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useEffect } from "react"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover"
import type { WooCommerceProduct } from "@/services/woo-commerce-service"
import { cn, getImageWithFallback } from "@/utilities"
import { SEARCH_MIN_LENGTH } from "./constants"
import {
  normalizeSearchQuery,
  type SearchUiState,
  useProductSearch,
} from "./hooks/use-product-search"
import { useSearchKeyboardNavigation } from "./hooks/use-search-keyboard-navigation"

export function renderProductPrice(product: WooCommerceProduct): string {
  return `R$ ${product.price}`
}

export function getProductImage(product: WooCommerceProduct): string | null {
  return product.images[0]?.thumbnail ?? null
}

export function getStoreName(product: WooCommerceProduct): string {
  return product.store?.shop_name ?? "Loja"
}

interface SearchProductsProps {
  variant?: "header" | "drawer"
  autoFocus?: boolean
}

interface SearchProductsInputProps {
  ariaExpanded: boolean
  isDrawer: boolean
  isSearching: boolean
  onClear: () => void
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onSearch: () => void
  onValueChange: (value: string) => void
  searchValue: string
  inputRef: React.RefObject<HTMLInputElement | null>
}

function SearchProductsInput({
  ariaExpanded,
  isDrawer,
  isSearching,
  onClear,
  onInputKeyDown,
  onSearch,
  onValueChange,
  searchValue,
  inputRef,
}: SearchProductsInputProps) {
  const showClearButton = searchValue.length > 0 && !isSearching

  return (
    <>
      <input
        aria-autocomplete='list'
        aria-controls='search-results'
        aria-expanded={ariaExpanded}
        aria-label='Pesquisar produtos'
        className={cn(
          "flex-1 bg-transparent px-3 outline-none placeholder:text-muted-foreground",
          isDrawer ? "text-base" : "text-sm"
        )}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={onInputKeyDown}
        placeholder='Buscar produtos...'
        ref={inputRef}
        role='combobox'
        type='search'
        value={searchValue}
      />

      <div className='flex items-center gap-2 pe-1'>
        {isSearching && (
          <div className='flex items-center justify-center px-2'>
            <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
          </div>
        )}

        {showClearButton && (
          <button
            aria-label='Limpar busca'
            className='flex items-center justify-center rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            onClick={onClear}
            type='button'
          >
            <X size={16} />
          </button>
        )}

        <button
          aria-label='Buscar'
          className={cn(
            "flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90",
            isDrawer ? "h-9 px-3" : "h-10 px-3"
          )}
          onClick={onSearch}
          type='button'
        >
          <SearchIcon size={16} />
        </button>
      </div>
    </>
  )
}

interface ProductResultItemProps {
  onSelect: (permalink: string) => void
  product: WooCommerceProduct
}

function ProductResultItem({ onSelect, product }: ProductResultItemProps) {
  const imageUrl = getProductImage(product)
  const storeName = getStoreName(product)
  const price = renderProductPrice(product)

  return (
    <CommandItem
      className='w-full cursor-pointer focus-within:bg-accent focus-within:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground'
      onSelect={() => onSelect(`/produtos/${product.slug}`)}
      value={product.id.toString()}
    >
      <Link
        className='flex w-full gap-3 p-2'
        href={`/produtos/${product.slug}`}
        tabIndex={-1}
      >
        {imageUrl && (
          <Image
            alt={product.name}
            className='h-12 w-12 rounded object-cover'
            height={48}
            src={getImageWithFallback(imageUrl)}
            width={48}
          />
        )}

        <div className='min-w-0 flex-1'>
          <p className='line-clamp-1 font-medium text-sm'>{product.name}</p>
          <p className='text-muted-foreground text-xs'>{storeName}</p>
          <p className='font-semibold text-sm'>{price}</p>
        </div>
      </Link>
    </CommandItem>
  )
}

interface SearchResultsProps {
  isDrawer: boolean
  isSearching: boolean
  onCommandKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
  onSelectProduct: (permalink: string) => void
  productsList: WooCommerceProduct[]
  searchUiState: SearchUiState
}

function SearchResults({
  isDrawer,
  isSearching,
  onCommandKeyDown,
  onSelectProduct,
  productsList,
  searchUiState,
}: SearchResultsProps) {
  const hasSearchError = searchUiState === "error"
  const isEmptyState = searchUiState === "empty"
  const hasProducts = searchUiState === "results"

  return (
    <Command
      className={cn(isDrawer ? "min-h-0 flex-1" : undefined)}
      id='search-results'
      shouldFilter={false}
    >
      <CommandList
        aria-busy={isSearching}
        className={cn(isDrawer ? "max-h-full flex-1" : undefined)}
        onKeyDown={onCommandKeyDown}
      >
        {isSearching && (
          <output
            aria-atomic='true'
            aria-live='polite'
            className='p-4 text-center text-muted-foreground text-sm'
          >
            Buscando produtos...
          </output>
        )}

        {hasSearchError && (
          <CommandEmpty className='py-6 text-center text-sm' role='alert'>
            Erro temporario ao buscar. Tente novamente.
          </CommandEmpty>
        )}

        {isEmptyState && (
          <CommandEmpty className='py-6 text-center text-sm'>
            Nenhum produto encontrado.
          </CommandEmpty>
        )}

        {hasProducts && (
          <CommandGroup>
            {productsList.map((product) => (
              <ProductResultItem
                key={product.id}
                onSelect={onSelectProduct}
                product={product}
              />
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  )
}

interface ViewAllLinkProps {
  onCommandKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
  searchValue: string
}

function ViewAllLink({ onCommandKeyDown, searchValue }: ViewAllLinkProps) {
  return (
    <div
      className='border-t p-3 text-center'
      onKeyDownCapture={onCommandKeyDown}
    >
      <Link
        className='font-medium text-primary text-sm hover:underline'
        href={`/produtos?query=${encodeURIComponent(searchValue)}`}
        tabIndex={0}
      >
        Ver todos os produtos
      </Link>
    </div>
  )
}

interface SearchInputShellProps {
  anchorRef?: React.RefObject<HTMLDivElement | null>
  className: string
  children: React.ReactNode
  variant: "drawer" | "popover"
}

function SearchInputShell({
  anchorRef,
  className,
  children,
  variant,
}: SearchInputShellProps) {
  if (variant === "popover") {
    return (
      <PopoverAnchor className={className} ref={anchorRef}>
        {children}
      </PopoverAnchor>
    )
  }

  return <div className={className}>{children}</div>
}

export function SearchProducts({
  variant = "header",
  autoFocus = false,
}: SearchProductsProps) {
  const isDrawer = variant === "drawer"
  const {
    open,
    setOpen,
    searchValue,
    productsList,
    isSearching,
    searchUiState,
    inputRef,
    anchorRef,
    popoverRef,
    handleSearchInput,
    handleClearInput,
    handleSearchClick,
    handleSelectProduct,
    performSearch,
  } = useProductSearch({ enableOutsideClose: !isDrawer })

  const { handleInputKeyDown, handleCommandKeyDown } =
    useSearchKeyboardNavigation({
      open,
      setOpen,
      searchValue,
      productsList,
      inputRef,
      performSearch,
    })

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus()
    }
  }, [autoFocus, inputRef])

  const hasProducts = productsList.length > 0
  const normalizedSearchValue = normalizeSearchQuery(searchValue)
  const hasSearchTerm = normalizedSearchValue.length >= SEARCH_MIN_LENGTH
  const hasActiveSearchState = searchUiState !== "idle"
  const shouldShowResults = isDrawer
    ? hasSearchTerm || isSearching || hasProducts || hasActiveSearchState
    : open

  const inputShellClassName = cn(
    "relative flex w-full items-center gap-0 overflow-hidden rounded-lg border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
    isDrawer ? "h-12 px-2" : "mx-auto h-13"
  )

  const input = (
    <SearchProductsInput
      ariaExpanded={isDrawer ? shouldShowResults : open}
      inputRef={inputRef}
      isDrawer={isDrawer}
      isSearching={isSearching}
      onClear={handleClearInput}
      onInputKeyDown={handleInputKeyDown}
      onSearch={handleSearchClick}
      onValueChange={handleSearchInput}
      searchValue={searchValue}
    />
  )

  const results = (
    <SearchResults
      isDrawer={isDrawer}
      isSearching={isSearching}
      onCommandKeyDown={handleCommandKeyDown}
      onSelectProduct={handleSelectProduct}
      productsList={productsList}
      searchUiState={searchUiState}
    />
  )

  const viewAllLink = hasProducts ? (
    <ViewAllLink
      onCommandKeyDown={handleCommandKeyDown}
      searchValue={searchValue}
    />
  ) : null

  return (
    <div className={cn("w-full", isDrawer ? "flex flex-1 flex-col gap-4" : "")}>
      {isDrawer ? (
        <>
          <SearchInputShell className={inputShellClassName} variant='drawer'>
            {input}
          </SearchInputShell>
          {shouldShowResults && (
            <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-input bg-background'>
              {results}
              {viewAllLink}
            </div>
          )}
        </>
      ) : (
        <Popover modal={false} onOpenChange={setOpen} open={open}>
          <SearchInputShell
            anchorRef={anchorRef}
            className={inputShellClassName}
            variant='popover'
          >
            {input}
          </SearchInputShell>
          <PopoverContent
            align='start'
            className='w-(--radix-popper-anchor-width) p-0'
            onOpenAutoFocus={(e) => {
              e.preventDefault()
            }}
            ref={popoverRef}
          >
            {results}
            {viewAllLink}
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
